import React, { useState, useMemo } from "react";
import { Col, Row } from "react-flexbox-grid";
import { Button, TextField } from "react-md";
import { Checkbox } from "@material-ui/core";
import { CART_SCREEN_STATE } from "../cartPaymentLinkConstants";
import EventService from "../../../../../../../service/event/EventService";

const fieldLabelStyle = {
  fontSize: "12px",
  color: "#888",
  marginBottom: "2px",
};

/**
 * Share-link screen of the cart approve flow. Reached only when the chosen
 * payment option needs a Razorpay link (external collection > 0). Collects the
 * notification channels and hands the paymentLink object to goNext, which then
 * calls /approve. Shape mirrors the backend PaymentLinkDetails (sendSms,
 * smsNumber, sendEmail, emailAddress, sendWhatsapp, whatsappNo, sendChat).
 */
const CartSelectShareLinkOptions = ({ goNext, profileDetails }) => {
  const phoneFromProfile = profileDetails?.phoneno
    ? String(profileDetails.phoneno)
    : "";
  const countryCodeFromProfile = profileDetails?.countryCode || 91;
  const emailFromProfile = profileDetails?.email || "";

  const [checkBoxWhatsapp, setCheckBoxWhatsapp] = useState(!!phoneFromProfile);
  const [checkBoxEmail, setCheckBoxEmail] = useState(!!emailFromProfile);
  const [checkBoxSMS, setCheckBoxSMS] = useState(true);
  const [checkBoxZylaChat, setCheckBoxZylaChat] = useState(true);

  const [whatsAppCC] = useState(countryCodeFromProfile);
  const [whatsAppPhoneNo, setWhatsAppPhoneNo] = useState(phoneFromProfile);

  const [smsCC] = useState(countryCodeFromProfile);
  const [smsPhoneNo, setSmsPhoneNo] = useState(phoneFromProfile);

  const [email, setEmail] = useState(emailFromProfile);

  const isAnyOptionSelected = useMemo(() => {
    return checkBoxWhatsapp || checkBoxEmail || checkBoxSMS || checkBoxZylaChat;
  }, [checkBoxWhatsapp, checkBoxEmail, checkBoxSMS, checkBoxZylaChat]);

  const validateAndProceed = () => {
    if (!isAnyOptionSelected) return;

    if (checkBoxWhatsapp) {
      if (!whatsAppPhoneNo || whatsAppPhoneNo.trim().length !== 10) {
        EventService.emit("showError", {
          message: "Please enter a valid 10-digit WhatsApp number",
        });
        return;
      }
    }

    if (checkBoxSMS) {
      if (!smsPhoneNo || smsPhoneNo.trim().length !== 10) {
        EventService.emit("showError", {
          message: "Please enter a valid 10-digit SMS number",
        });
        return;
      }
    }

    if (checkBoxEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email || !emailRegex.test(email.trim())) {
        EventService.emit("showError", {
          message: "Please enter a valid email address",
        });
        return;
      }
    }

    const paymentLink = {
      sendWhatsapp: checkBoxWhatsapp,
      whatsappNo: checkBoxWhatsapp
        ? { countryCode: whatsAppCC, phoneNo: Number(whatsAppPhoneNo) }
        : null,

      sendSms: checkBoxSMS,
      smsNumber: checkBoxSMS
        ? { countryCode: smsCC, phoneNo: Number(smsPhoneNo) }
        : null,

      sendEmail: checkBoxEmail,
      emailAddress: checkBoxEmail ? email : null,

      sendChat: checkBoxZylaChat,
    };

    goNext(CART_SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK, paymentLink);
  };

  return (
    <div className="pld-screen pld-method-container">
      <span className="pld-title-center">
        Would you like to send the payment link to user?
      </span>

      {/* SMS */}
      <Row className="pld-row-spacing">
        <Col xs={1} />
        <Col xs={4}>
          <div className="pld-option-row">
            <div className="pld-checkbox-wrapper">
              <Checkbox
                id="cart-link-share-sms"
                color="default"
                checked={checkBoxSMS}
                onChange={() => setCheckBoxSMS(!checkBoxSMS)}
                disabled={true}
              />
            </div>
            <div className="pld-option-label">Send to SMS</div>
          </div>
        </Col>
        <Col xs={5}>
          <div>
            <div style={fieldLabelStyle}>Patient number</div>
            <div className="pld-phone-container">
              <div className="pld-phone-cc">
                <TextField value={`+${smsCC}`} disabled lineDirection="left" />
              </div>
              <div className="pld-phone-input">
                <TextField
                  value={smsPhoneNo}
                  lineDirection="left"
                  onChange={(value) => {
                    if (/^\d{0,10}$/.test(value)) setSmsPhoneNo(value);
                  }}
                />
              </div>
            </div>
          </div>
        </Col>
        <Col xs={2} />
      </Row>

      {/* WHATSAPP */}
      <Row className="pld-row-spacing">
        <Col xs={1} />
        <Col xs={4}>
          <div className="pld-option-row">
            <div className="pld-checkbox-wrapper">
              <Checkbox
                id="cart-link-share-whatsapp"
                color="secondary"
                checked={checkBoxWhatsapp}
                onChange={() => setCheckBoxWhatsapp(!checkBoxWhatsapp)}
              />
            </div>
            <div className="pld-option-label">Send to WhatsApp</div>
          </div>
        </Col>
        <Col xs={5}>
          <div>
            <div style={fieldLabelStyle}>Patient number</div>
            <div className="pld-phone-container">
              <div className="pld-phone-cc">
                <TextField
                  value={`+${whatsAppCC}`}
                  disabled
                  lineDirection="left"
                />
              </div>
              <div className="pld-phone-input">
                <TextField
                  value={whatsAppPhoneNo}
                  disabled={!checkBoxWhatsapp}
                  lineDirection="left"
                  onChange={(value) => {
                    if (/^\d{0,10}$/.test(value)) setWhatsAppPhoneNo(value);
                  }}
                />
              </div>
            </div>
          </div>
        </Col>
        <Col xs={2} />
      </Row>

      {/* EMAIL */}
      <Row className="pld-row-spacing">
        <Col xs={1} />
        <Col xs={4}>
          <div className="pld-option-row">
            <div className="pld-checkbox-wrapper">
              <Checkbox
                id="cart-link-share-email"
                color="secondary"
                checked={checkBoxEmail}
                onChange={() => setCheckBoxEmail(!checkBoxEmail)}
              />
            </div>
            <div className="pld-option-label">Send to Email</div>
          </div>
        </Col>
        <Col xs={5}>
          <div>
            <div style={fieldLabelStyle}>E-mail</div>
            <TextField
              value={email}
              type="text"
              placeholder="Type here"
              disabled={!checkBoxEmail}
              onChange={(e) => {
                if (e.length <= 50) setEmail(e);
              }}
            />
          </div>
        </Col>
        <Col xs={2} />
      </Row>

      {/* ZYLA CHAT */}
      <Row className="pld-row-spacing">
        <Col xs={1} />
        <Col xs={4}>
          <div className="pld-option-row">
            <div className="pld-checkbox-wrapper">
              <Checkbox
                id="cart-link-share-chat"
                color="secondary"
                checked={checkBoxZylaChat}
                onChange={() => setCheckBoxZylaChat(!checkBoxZylaChat)}
              />
            </div>
            <div className="pld-option-label">Send to Zyla chat</div>
          </div>
        </Col>
        <Col xs={5}>
          <div>
            <div style={fieldLabelStyle}>Patient ID</div>
            <TextField
              value={profileDetails?.id ? String(profileDetails.id) : ""}
              type="text"
              placeholder=""
              disabled
            />
          </div>
        </Col>
        <Col xs={2} />
      </Row>

      {/* NOTE */}
      <div className="pld-note-container">
        <div className="pld-note-box">
          <span className="pld-bold">Note: </span>
          The selected payment preferences cannot be modified after clicking{" "}
          <span className="pld-bold">'Approve &amp; share payment link'</span>
        </div>
      </div>

      <div className="pld-center">
        <Button
          raised
          disabled={!isAnyOptionSelected}
          className="zy-btn-orange pld-main-btn"
          onClick={validateAndProceed}
        >
          Approve &amp; share payment link
        </Button>
      </div>
    </div>
  );
};

export default CartSelectShareLinkOptions;
